/**
 * MyRequestsList Component
 *
 * Displays list of user's feature requests with filtering, sorting, and status display.
 *
 * @module components/feedback/MyRequestsList
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  TextField,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Add as AddIcon,
  Comment as CommentIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/AuthContext';
import { FeatureRequestService } from '../../services/featureRequestService';
import {
  FeatureRequest,
  RequestStatus,
  STATUS_BADGES,
  REQUEST_TYPES,
} from '../../types/featureRequest';
import { formatDistanceToNow } from 'date-fns';

/**
 * MyRequestsList - Display user's feature requests
 */
const MyRequestsList: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();

  // State
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<RequestStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');

  /**
   * Fetch user's requests
   */
  useEffect(() => {
    fetchRequests();
  }, [user]);

  /**
   * Apply filters and sorting
   */
  useEffect(() => {
    applyFiltersAndSort();
  }, [requests, filterStatus, sortBy]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      // Always use authenticated user identifier if available
      if (!user) {
        setError('Please log in to view your requests');
        setLoading(false);
        return;
      }

      const identifier = user.uid || user.email || '';
      if (!identifier) {
        setError('Unable to identify user. Please try logging in again.');
        setLoading(false);
        return;
      }

      const userRequests = await FeatureRequestService.getMyRequests(identifier);
      setRequests(userRequests);
    } catch (err: any) {
      console.error('Error fetching requests:', err);
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...requests];

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(req => req.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortBy === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredRequests(filtered);
  };

  /**
   * Navigate to request detail
   */
  const handleViewRequest = (requestId: string) => {
    navigate(`/feedback/request/${requestId}`);
  };

  /**
   * Navigate to submit form
   */
  const handleNewRequest = () => {
    navigate('/feedback/submit');
  };

  /**
   * Format date
   */
  const formatDate = (dateString: string): string => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  /**
   * Get status chip
   */
  const getStatusChip = (status: RequestStatus) => {
    const config = STATUS_BADGES[status];
    return (
      <Chip
        label={
          <Box display="flex" alignItems="center" gap={0.5}>
            <span>{config.icon}</span>
            <span>{config.label}</span>
          </Box>
        }
        size="small"
        sx={{
          bgcolor: config.color,
          color: 'white',
          fontWeight: 'bold',
        }}
      />
    );
  };

  /**
   * Get type chip
   */
  const getTypeChip = (type: string) => {
    const config = REQUEST_TYPES[type as keyof typeof REQUEST_TYPES];
    return (
      <Chip
        label={`${config.icon} ${config.label}`}
        size="small"
        variant="outlined"
      />
    );
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box maxWidth={800} mx="auto" p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
        <Button onClick={fetchRequests} sx={{ mt: 2 }}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box maxWidth={1200} mx="auto" p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            My Feature Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track the status of your submitted requests
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNewRequest}
          size="large"
        >
          New Request
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Filter by Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as RequestStatus | 'all')}
              fullWidth
              size="small"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {Object.entries(STATUS_BADGES).map(([status, config]) => (
                <MenuItem key={status} value={status}>
                  {config.icon} {config.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Sort by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest')}
              fullWidth
              size="small"
            >
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Empty state */}
      {requests.length === 0 && (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No requests yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Submit your first feature request or bug report to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewRequest}
            size="large"
          >
            Submit Your First Request
          </Button>
        </Paper>
      )}

      {/* Requests List */}
      {filteredRequests.length > 0 && (
        <Grid container spacing={2}>
          {filteredRequests.map((request) => (
            <Grid item xs={12} key={request.id}>
              <Card
                elevation={2}
                sx={{
                  '&:hover': {
                    elevation: 4,
                    bgcolor: 'action.hover',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box flex={1}>
                      {/* Status and Type */}
                      <Stack direction="row" spacing={1} mb={1}>
                        {getStatusChip(request.status)}
                        {getTypeChip(request.type)}
                      </Stack>

                      {/* Title */}
                      <Typography variant="h6" gutterBottom>
                        {request.title}
                      </Typography>

                      {/* Description Preview */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {request.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Metadata */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2}>
                      <Typography variant="caption" color="text.secondary">
                        Submitted: {formatDate(request.createdAt)}
                      </Typography>
                      {request.completedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Completed: {formatDate(request.completedAt)}
                        </Typography>
                      )}
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      {request.screenshotCount > 0 && (
                        <Tooltip title={`${request.screenshotCount} screenshot(s)`}>
                          <Chip
                            icon={<ImageIcon />}
                            label={request.screenshotCount}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {request.commentCount > 0 && (
                        <Tooltip title={`${request.commentCount} comment(s)`}>
                          <Chip
                            icon={<CommentIcon />}
                            label={request.commentCount}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>

                  {/* Developer Response */}
                  {request.publicResponse && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption" display="block" fontWeight="bold">
                        Developer Response:
                      </Typography>
                      <Typography variant="body2">{request.publicResponse}</Typography>
                    </Alert>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewRequest(request.id)}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* No results after filtering */}
      {requests.length > 0 && filteredRequests.length === 0 && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No requests match your filters
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters to see more results
          </Typography>
          <Button onClick={() => setFilterStatus('all')} sx={{ mt: 2 }}>
            Clear Filters
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default MyRequestsList;
